import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import { gray40, gray80, white } from '../../../constants/colors';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { boldMd, dialogText, dialogTitle } from '../../../constants/location';

type Props = {
  open: boolean;
  onClose: () => void;
  onContact: () => void;
  title: string;
  infoText?: string;
  descriptionLines: string[];
  hideCancelButton?: boolean;
  contactButtonText?: string;
  onSecondaryAction?: () => void;
  secondaryActionText?: string;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    '&:hover': {
      backgroundColor: white,
      border: `1px solid ${gray80}`,
      boxShadow: 'none',
    },
    backgroundColor: white,
    border: `1px solid ${gray80}`,
    boxShadow: 'none',
    color: gray80,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  contactButton: {
    '&:hover': {
      backgroundColor: '#F03C0A',
      boxShadow: 'none',
    },
    backgroundColor: '#F03C0A',
    boxShadow: 'none',
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  contactButtonFullWidth: {
    '&:hover': {
      backgroundColor: '#F03C0A',
      boxShadow: 'none',
    },
    backgroundColor: '#F03C0A',
    boxShadow: 'none',
    height: '44px',
    width: '100%',
  },
  contactButtonText: {
    ...boldMd,
    color: white,
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...dialogTitle,
    '@media screen and (max-width:960px)': {
      ...boldMd,
    },
  },
  infoText: {
    ...dialogText,
    marginBottom: '16px',
  },
  descriptionText: {
    ...dialogText,
    marginBottom: '16px',
    whiteSpace: 'pre-line',
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
};

const AccountManagementDialog = ({
  open,
  onClose,
  onContact,
  title,
  infoText,
  descriptionLines,
  hideCancelButton = false,
  contactButtonText = 'お問い合わせ',
  onSecondaryAction,
  secondaryActionText,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{title}</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      {infoText && <Typography sx={styles.infoText}>{infoText}</Typography>}
      {descriptionLines.map((line, index) => (
        <Typography key={index} sx={styles.descriptionText}>
          {line}
        </Typography>
      ))}
      <Box sx={styles.buttonWrapper}>
        {!hideCancelButton && (
          <Button sx={styles.cancelButton} onClick={onClose}>
            <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
          </Button>
        )}
        {onSecondaryAction && secondaryActionText && (
          <Button sx={styles.cancelButton} onClick={onSecondaryAction}>
            <Typography sx={styles.cancelButtonText}>{secondaryActionText}</Typography>
          </Button>
        )}
        <Button
          sx={hideCancelButton ? styles.contactButtonFullWidth : styles.contactButton}
          onClick={onContact}
        >
          <Typography sx={styles.contactButtonText}>{contactButtonText}</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default AccountManagementDialog;
